import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useTranslation } from "@/translations";

import { BulkAiEditor } from "./BulkAiEditor";
import { PortfolioIntegrationWorkspace } from "./PortfolioIntegrationWorkspace";
import { AiProjectSetupQueue } from "./ProjectSetupQueue";
import { usePortfolioAiProjects } from "./usePortfolioIntegrationProjects";

/** Manages portfolio-wide AI prompts and model profiles for every project the user can configure. */
export function AiPortfolioIntegrationPage(): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const projects = usePortfolioAiProjects();

  if (projects.error) {
    return <ErrorPage error={projects.error} />;
  }
  if (projects.isLoading) {
    return <FullSizeLoader withPadding />;
  }
  if (!sessionUser.isSuperAdmin && projects.rows.length === 0) {
    return <ErrorPage status={403} />;
  }

  return (
    <PortfolioIntegrationWorkspace
      name={t("page.portfolio-integrations.ai.title")}
      description={t("page.portfolio-integrations.ai.workspace-description")}
      rows={projects.rows}
      showIntegrationStatus={false}
      renderBulkEditor={({ rows, selectedProjectIds, onContinueToProjectSetup }) => (
        <BulkAiEditor
          key={[...selectedProjectIds].sort().join(",")}
          rows={rows}
          selectedProjectIds={selectedProjectIds}
          onContinueToProjectSetup={onContinueToProjectSetup}
        />
      )}
      renderProjectSetup={({ rows, selectedProjectIds }) => (
        <AiProjectSetupQueue rows={rows} selectedProjectIds={selectedProjectIds} />
      )}
    />
  );
}
